import React, { useState, useEffect } from "react";
import { DataTable as Table, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { AlertTriangle, Eye } from "lucide-react";
import { useDamagedItems } from "../hooks/useDamagedItems";

export const DAMAGED_ITEMS_HEADERS = [
    "Return ID", 
    "Date", 
    "Type", 
    "Project ID",
    "Damaged Items", 
    "Total Damaged Qty", 
    "Damaged Value", 
    "Action"
];

const DamagedItemRow = ({ r, processedData, onView }) => {
    const { enrichedDamagedItems, totalDamagedQty, totalDamagedAmount } = processedData;

    return (
        <tr className="hover:bg-slate-50/50 transition-colors text-[11px] font-['Poppins']">
            {/* Return ID */}
            <td className="td">
                <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${r.rawType === 'purchase' ? 'bg-emerald-50 text-emerald-500' : 'bg-red-50 text-red-500'} border border-current/10`}>
                        <AlertTriangle size={14} />
                    </div>
                    <span className="font-normal text-slate-700 uppercase tracking-tight">{r.returnId}</span>
                </div>
            </td>

            {/* Date */}
            <td className="td font-normal text-slate-500">
                {r.date ? new Date(r.date).toLocaleDateString() : "N/A"}
            </td>

            {/* Type */}
            <td className="td">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-normal uppercase tracking-widest border ${r.rawType === 'purchase'
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                    : 'bg-red-50 text-red-600 border-red-100'
                    }`}>
                    {r.returnType}
                </span>
            </td>

            {/* Project ID */}
            <td className="td">
                <span className="text-[10px] font-normal uppercase tracking-tight text-[#f97316] bg-blue-50 w-fit px-2 py-0.5 rounded border border-blue-100">
                    {r.projectDisplayId || r.projectId || "—"}
                </span>
            </td>

            {/* Damaged Items */}
            <td className="td">
                <div className="flex flex-col gap-1">
                    {enrichedDamagedItems.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className="font-normal text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                {Number(item.damagedQty) || 0}
                            </span>
                            <span className="text-slate-600 font-normal truncate max-w-[120px]">
                                {item.item?.name || item.itemName || item.name || "Unknown Item"}
                            </span>
                            {item.reason && (
                                <span className="text-red-400 text-[9px] italic truncate">
                                    ({item.reason})
                                </span>
                            )}
                        </div>
                    ))}
                    {enrichedDamagedItems.length > 2 && (
                        <span className="text-red-400 text-[9px] font-normal">
                            +{enrichedDamagedItems.length - 2} more damaged items
                        </span>
                    )}
                    {enrichedDamagedItems.length === 0 && (
                        <span className="text-slate-400 text-[9px] italic">
                            No damaged items found
                        </span>
                    )}
                </div>
            </td>

            {/* Total Damaged Qty */}
            <td className="td">
                <span className="px-3 py-1 bg-red-50 text-red-600 rounded-lg font-normal text-[12px] border border-red-100">
                    {totalDamagedQty || 0}
                </span>
            </td>

            {/* Damaged Value */}
            <td className="td font-normal text-slate-700">
                <span className={totalDamagedAmount > 0 ? "text-red-500" : "text-slate-400"}>
                    ₹{fmt(totalDamagedAmount)}
                </span>
            </td>

            {/* Action */}
            <td className="td">
                <div className="flex gap-1.5 items-center">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onView) onView(r);
                        }}
                        className="p-1.5 text-slate-400 hover:text-[#2b448f] hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        title="View Return Details"
                    >
                        <Eye size={16} />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export const DamagedItemsTable = ({ returns, onView }) => {
    const { damagedReturns, processDamagedReturn } = useDamagedItems(returns);
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        console.log("[DEBUG] DamagedItemsTable rendered.");
        console.log("[DEBUG] Input returns:", returns);
        console.log("[DEBUG] output damagedReturns:", damagedReturns);
        setPage(1);
    }, [damagedReturns, returns]);

    if (damagedReturns.length === 0) return (
        <div className="p-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <AlertTriangle className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-400 font-normal">No damaged items found</p>
            <p className="text-slate-400 text-xs mt-2">
                Damaged items will appear here when returns are processed with damage reports
            </p>
            <p className="text-red-500 text-xs mt-4">
                DEBUG INFO: Received {returns ? returns.length : 0} total returns from API.
            </p>
        </div>
    );

    const currentData = damagedReturns.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const totalPages = Math.max(Math.ceil(damagedReturns.length / itemsPerPage), 1);

    return (
        <div className="card rounded-none overflow-hidden border-slate-100 shadow-sm">
            <Table headers={DAMAGED_ITEMS_HEADERS}>
                {currentData.map(r => {
                    const processedData = processDamagedReturn(r);
                    return (
                        <DamagedItemRow 
                            key={`damaged-${r.rawType}-${r._id}`} 
                            r={r} 
                            processedData={processedData} 
                            onView={onView} 
                        />
                    );
                })}
            </Table>
            
            <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
            />
        </div>
    );
};
